import React from "react";
import { useTranslation } from "react-i18next";

interface PromptResizeHandleProps {
  isResizing: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onKeyboardResize: (delta: number) => void;
  onDoubleClick: () => void;
  /** Current height in px, or null for auto */
  valueNow?: number | null;
  /** Minimum allowed height in px */
  valueMin: number;
  /** Maximum allowed height in px */
  valueMax: number;
}

const KEYBOARD_STEP = 20; // pixels per keypress (absolute, since height uses px not %)

/**
 * A horizontal draggable resize handle for adjusting the prompt input area height.
 * Mirrors the visual style of the vertical ResizeHandle used for the artefacts panel.
 * Keyboard accessible: up/down arrows adjust height.
 * Double-click resets to auto sizing.
 */
export const PromptResizeHandle: React.FC<PromptResizeHandleProps> = ({
  isResizing,
  onMouseDown,
  onKeyboardResize,
  onDoubleClick,
  valueNow,
  valueMin,
  valueMax,
}) => {
  const { t } = useTranslation();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const delta = e.key === "ArrowUp" ? KEYBOARD_STEP : -KEYBOARD_STEP;

      onKeyboardResize(delta);
    }
  };

  return (
    <div
      aria-label={t("Resize input area")}
      aria-orientation="horizontal"
      aria-valuemax={Math.round(valueMax)}
      aria-valuemin={Math.round(valueMin)}
      aria-valuenow={valueNow != null ? Math.round(valueNow) : undefined}
      className="relative cursor-row-resize flex items-center justify-center my-3 focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:outline-none"
      data-testid="prompt-resize-handle"
      role="separator"
      tabIndex={0}
      onDoubleClick={onDoubleClick}
      onKeyDown={handleKeyDown}
      onMouseDown={onMouseDown}
    >
      {/* Thin horizontal line - matches the vertical divider style */}
      <div className={`h-px w-full bg-divider ${isResizing ? "bg-default-300" : ""}`} />
      {/* Grab handle - centred pill with grip lines */}
      <div
        className={`absolute flex flex-col gap-0.5 py-1.5 px-2 rounded transition-colors duration-150 ease-out ${isResizing ? "bg-default-200" : "bg-default-100 hover:bg-default-200"}`}
      >
        <div className="h-px w-1 bg-default-400" />
        <div className="h-px w-1 bg-default-400" />
        <div className="h-px w-1 bg-default-400" />
      </div>
    </div>
  );
};
